// Video Director — pure EDL (edit decision list) ops + doc helpers.
// Spec: aidea-middleware-vd/docs/superpowers/specs/2026-10-15-0345-video-director-mvp-design.md §4, §6.
//
// Coordinates are frames of the NORMALIZED take (24 fps CFR). A segment plays
// take frames [inFrame, outFrame) — half-open. Its timeline position is the sum
// of the lengths before it: positions are derived, never stored, so every op
// ripples implicitly. Every op returns a NEW array, returns the INPUT unchanged
// when it is a no-op, and never breaks an invariant (≥ 1 frame per segment,
// ≤ MAX_SEGMENTS segments, ≤ MAX_TOTAL_FRAMES frames in total). The server
// re-checks take membership/readiness with validateEdl.

/**
 * Mirror of the middleware's video-director.types.ts (spec §4) — keep in sync.
 * @typedef {"16:9"|"9:16"} Aspect
 * @typedef {"pending"|"ready"|"failed"} ItemStatus
 * @typedef {"prefilling"|"brief"|"treating"|"treatment"|"storyboard"|"editing"} ProjectStatus
 * @typedef {{learningGoal: string, audience: string, narration: string, language: string,
 *   targetSec: number, aspect: Aspect, styleKey: (string|null), customStyle: string,
 *   cast: string, locations: string, criticalActions: string, brandPolicy: string,
 *   required: string[]}} Brief
 * @typedef {{url: (string|null), status: ItemStatus, prompt: string, error?: string,
 *   createdAt?: string}} Still
 * @typedef {{id: string, name: string, description: string, still: Still}} CastMember
 * @typedef {{id: string, name: string, description: string, still: Still}} Location
 * @typedef {{logline: string, look: string, cast: CastMember[], locations: Location[]}} Bible
 * @typedef {{id: string, url: (string|null), frames: number, fps: 24, status: ItemStatus,
 *   error?: string, source: ("keyframe"|"continue"|"import"), fromTakeId?: string, fromFrame?: number,
 *   afterSegmentId?: string, prompt: string, jobId: string, createdAt: string}} Take
 * @typedef {{id: string, narration: string, durationSec: number, camera: string, prompt: string,
 *   castIds: string[], locationId: (string|null), keyframe: (Still & {approved: boolean}),
 *   takes: Take[]}} Shot
 * @typedef {{id: string, shotId: string, takeId: string, inFrame: number, outFrame: number,
 *   transition?: ("crossfade"|"fade"), zoom?: ("in"|"out")}} EdlSegment
 * @typedef {{id: string, url: (string|null), status: ItemStatus, source: ("frame"|"upload"|"generated"),
 *   fromTakeId?: string, frame?: number, prompt?: string, parentRefId?: string, model?: string,
 *   error?: string, suggestedPrompt?: string, policy?: {adjusted: boolean, originalPrompt: string}, createdAt: string}} Ref
 * @typedef {{url: (string|null), status: ItemStatus, jobId: string, createdAt: string,
 *   error?: string}} Render
 * @typedef {{id: string, trainingId: string, navId: string, version: number,
 *   status: ProjectStatus, error: (string|null), brief: Brief, bible: (Bible|null),
 *   shots: Shot[], edl: EdlSegment[], refs?: Ref[], render: (Render|null)}} VideoProject
 */

export const FPS = 24;
export const TAKE_SEC = 8; // spec decision 1: every take is 8 s (192 frames before normalizing)
export const MAX_TOTAL_FRAMES = 120 * FPS; // 2880
export const MAX_SEGMENTS = 200;
export const MAX_REFS = 24; // reference set cap (server refs.full)
export const HISTORY_CAP = 50;
// A take/render/still pending after this long means its worker died (server PENDING_STALE_MS, 65 min).
export const STALE_MS = 65 * 60 * 1000;

const len = (s) => s.outFrame - s.inFrame;
// A segment that starts at a NEW cut carries no transition (it belongs to the cut into the original).
const withoutTransition = ({ transition, ...rest }) => rest; // eslint-disable-line no-unused-vars

// Director transition length (0.5 s) — mirror of the middleware's EDL_TRANSITION_FRAMES.
export const TRANSITION_FRAMES = 12;

export const newSegId = () =>
  `seg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/** @param {EdlSegment[]} edl */
export function totalFrames(edl) {
  return edl.reduce((n, s) => n + len(s), 0);
}

/** Timeline frame where edl[index] starts. */
export const CLIP_VOLUME_DEFAULT = 0.3; // project.clipAudio absent -> unmuted at this level (mirror of the middleware)

/** 0..1 clip-sound level of a project's clipAudio ({ muted?, volume? }). */
export const clipLevel = (clipAudio) =>
  clipAudio?.muted ? 0 : (clipAudio?.volume ?? CLIP_VOLUME_DEFAULT);

/**
 * Identity of a cut; the middleware stores the same string on `render.edlSignature` at render time.
 * Effect tokens only when present; the clip-sound token only when a used take has an audio track.
 */
export const edlSignature = (edl, shots, clipAudio) =>
  edl
    .map(
      (s) =>
        `${s.takeId}:${s.inFrame}-${s.outFrame}` +
        (s.transition ? `~x:${s.transition}` : "") +
        (s.zoom ? `~z:${s.zoom}` : "")
    )
    .join("|") +
  (shots &&
  edl.some((s) => shots.some((sh) => sh.takes.some((tk) => tk.id === s.takeId && tk.audioTrack)))
    ? `|a:${clipLevel(clipAudio) === 0 ? "mute" : clipLevel(clipAudio).toFixed(2)}`
    : "");

/**
 * What the render does at the cut INTO edl[index]: null | "fade" | "crossfade". A crossfade needs
 * TRANSITION_FRAMES spare frames of the previous take after its outFrame (`takeFrames(seg)`), else it
 * renders as a fade; segments of ≤ TRANSITION_FRAMES frames skip the transition. Mirrors the server.
 */
export function effectiveTransition(edl, index, takeFrames) {
  const seg = edl[index];
  const prev = edl[index - 1];
  if (!prev || !seg.transition || len(seg) <= TRANSITION_FRAMES || len(prev) <= TRANSITION_FRAMES)
    return null;
  return seg.transition === "crossfade" &&
    (takeFrames(prev) ?? 0) - prev.outFrame >= TRANSITION_FRAMES
    ? "crossfade"
    : "fade";
}

export function segmentStart(edl, index) {
  let frame = 0;
  for (let i = 0; i < index; i += 1) frame += len(edl[i]);
  return frame;
}

/** The segment playing at `timelineFrame`, or null outside the video. */
export function locate(edl, timelineFrame) {
  if (!(timelineFrame >= 0)) return null;
  let start = 0;
  for (let index = 0; index < edl.length; index += 1) {
    const segment = edl[index];
    const end = start + len(segment);
    if (timelineFrame < end) return { index, segment, localFrame: timelineFrame - start };
    start = end;
  }
  return null;
}

/** Move one or both edges, clamped to [0, takeFrames], ≥ 1 frame, total ≤ MAX_TOTAL_FRAMES. */
export function trimSegment(edl, segId, { inFrame, outFrame } = {}, takeFrames) {
  const index = edl.findIndex((s) => s.id === segId);
  if (index < 0 || !(takeFrames >= 1)) return edl;
  const seg = edl[index];
  const movingIn = Number.isInteger(inFrame);
  const movingOut = Number.isInteger(outFrame);
  let a = Math.max(0, Math.min(movingIn ? inFrame : seg.inFrame, takeFrames - 1));
  let b = Math.max(1, Math.min(movingOut ? outFrame : seg.outFrame, takeFrames));
  if (b <= a) {
    if (movingIn && !movingOut) a = b - 1;
    else b = a + 1;
  }
  const room = MAX_TOTAL_FRAMES - (totalFrames(edl) - len(seg));
  if (b - a > room) {
    if (movingIn && !movingOut) a = b - room;
    else b = a + room;
  }
  if (a === seg.inFrame && b === seg.outFrame) return edl;
  const out = edl.slice();
  out[index] = { ...seg, inFrame: a, outFrame: b };
  return out;
}

/** Set (or clear, value falsy) a segment's `transition` ("crossfade"|"fade") or `zoom` ("in"|"out"). */
export function setEffect(edl, segId, key, value) {
  const index = edl.findIndex((s) => s.id === segId);
  if (index < 0 || (edl[index][key] ?? null) === (value || null)) return edl;
  const { [key]: _old, ...rest } = edl[index]; // eslint-disable-line no-unused-vars
  const out = [...edl];
  out[index] = value ? { ...rest, [key]: value } : rest;
  return out;
}

/** One segment → two on the same take, cut at the playhead. */
export function splitAt(edl, timelineFrame, newId) {
  const hit = locate(edl, timelineFrame);
  if (!hit || hit.localFrame === 0 || edl.length >= MAX_SEGMENTS) return edl;
  const { index, segment, localFrame } = hit;
  const cut = segment.inFrame + localFrame;
  return [
    ...edl.slice(0, index),
    { ...segment, outFrame: cut },
    { ...withoutTransition(segment), id: newId, inFrame: cut },
    ...edl.slice(index + 1),
  ];
}

/** Remove timeline frames [from, to) — may span segments; ends are trimmed, the middle dropped. */
export function removeRange(edl, fromFrame, toFrame, newId) {
  const a = Math.max(0, Math.min(fromFrame, toFrame));
  const b = Math.min(totalFrames(edl), Math.max(fromFrame, toFrame));
  if (!(b > a)) return edl;
  const out = [];
  let start = 0;
  for (const seg of edl) {
    const end = start + len(seg);
    if (end <= a || start >= b) {
      out.push(seg);
    } else {
      const head = Math.max(0, a - start);
      const tail = Math.max(0, end - b);
      if (head > 0) out.push({ ...seg, outFrame: seg.inFrame + head });
      if (tail > 0)
        out.push({
          ...(head > 0 ? withoutTransition(seg) : seg),
          id: head > 0 ? newId : seg.id,
          inFrame: seg.outFrame - tail,
        });
    }
    start = end;
  }
  return out.length > MAX_SEGMENTS ? edl : out;
}

/** Drop everything at/after `timelineFrame`. */
export function cutAfter(edl, timelineFrame) {
  const f = Math.max(0, timelineFrame);
  if (f >= totalFrames(edl)) return edl;
  const out = [];
  let start = 0;
  for (const seg of edl) {
    const end = start + len(seg);
    if (end <= f) out.push(seg);
    else if (start < f) out.push({ ...seg, outFrame: seg.inFrame + (f - start) });
    start = end;
  }
  return out;
}

export function moveSegment(edl, segId, toIndex) {
  const from = edl.findIndex((s) => s.id === segId);
  if (from < 0) return edl;
  const to = Math.max(0, Math.min(toIndex, edl.length - 1));
  if (to === from) return edl;
  const out = edl.slice();
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved);
  return out;
}

export function deleteSegment(edl, segId) {
  const out = edl.filter((s) => s.id !== segId);
  return out.length === edl.length ? edl : out;
}

/** Swap the take under a segment, keeping in/out (clamped to the new take). */
export function selectTake(edl, segId, takeId, takeFrames) {
  const index = edl.findIndex((s) => s.id === segId);
  if (index < 0 || !(takeFrames >= 1) || edl[index].takeId === takeId) return edl;
  const seg = edl[index];
  const inFrame = Math.min(seg.inFrame, takeFrames - 1);
  const outFrame = Math.max(inFrame + 1, Math.min(seg.outFrame, takeFrames));
  const out = edl.slice();
  out[index] = { ...seg, takeId, inFrame, outFrame };
  return out;
}

/**
 * Where a clip dragged to `newStart` (timeline frames) lands: the number of
 * OTHER clips, at their on-screen positions, whose middle is left of the
 * dragged clip's middle. Feed it to moveSegment. -1 for an unknown id.
 */
export function dropIndex(edl, segId, newStart) {
  const moving = edl.find((s) => s.id === segId);
  if (!moving) return -1;
  const mid = newStart + len(moving) / 2;
  let start = 0;
  let index = 0;
  for (const seg of edl) {
    const l = len(seg);
    if (seg.id !== segId && start + l / 2 < mid) index += 1;
    start += l;
  }
  return index;
}

// ── Local undo/redo stack (component state, never sent to the server) ────────
export const emptyHistory = Object.freeze({ past: [], future: [] });

export function pushHistory(history, edl) {
  return { past: [...history.past, edl].slice(-HISTORY_CAP), future: [] };
}

export function undo(history, current) {
  if (!history.past.length) return null;
  return {
    edl: history.past[history.past.length - 1],
    history: {
      past: history.past.slice(0, -1),
      future: [current, ...history.future].slice(0, HISTORY_CAP),
    },
  };
}

export function redo(history, current) {
  if (!history.future.length) return null;
  const [edl, ...future] = history.future;
  return { edl, history: { past: [...history.past, current].slice(-HISTORY_CAP), future } };
}

// ── Doc helpers ───────────────────────────────────────────────────────────────

/**
 * A pending item (take, render, still, keyframe) whose worker should have
 * finished long ago — shown as failed + Retry. No createdAt → never stale.
 */
export function isStale(item, now = Date.now()) {
  return (
    !!item &&
    item.status === "pending" &&
    !!item.createdAt &&
    now - Date.parse(item.createdAt) > STALE_MS
  );
}

/** True while a worker still owes the doc something → keep polling (spec decision 8). */
export function hasPendingWork(project, now = Date.now()) {
  if (!project) return false;
  if ((project.status === "prefilling" || project.status === "treating") && !project.error) {
    return true;
  }
  const pending = (item) => !!item && item.status === "pending" && !isStale(item, now);
  const members = project.bible ? [...project.bible.cast, ...project.bible.locations] : [];
  // Keyframes only exist once the treatment is confirmed.
  const keyframesLive = project.status === "storyboard" || project.status === "editing";
  return (
    members.some((m) => pending(m.still)) ||
    project.shots.some((s) => (keyframesLive && pending(s.keyframe)) || s.takes.some(pending)) ||
    (project.refs || []).some(pending) ||
    pending(project.render)
  );
}

/** "Replace File" on a Director-made video box reopens VideoModal (Director tab), not the Library. */
export const isDirectorVideo = (state) => !!state?.videoProjectId;

/** "Edit in Director": a plain video (has a file, no Director project yet). */
export const canEditInDirector = (state) => !!state?.url && !isDirectorVideo(state);

/** A shot made by continue re-runs from its source (R30); an imported shot cannot be regenerated. */
export const isContinueShot = (shot) => !!shot?.takes?.some((tk) => tk.source === "continue");
export const isImportShot = (shot) => !!shot?.takes?.some((tk) => tk.source === "import");
